/**
 * Version-1 QR (21×21), byte mode, ECC-M, mask 0.
 * Sized for the 6-character handover code — no native QR dependency.
 */

const SIZE = 21
const POLY = 0x11d

function gfMul(a: number, b: number): number {
  let p = 0
  for (let i = 0; i < 8; i += 1) {
    if (b & 1) p ^= a
    const hi = a & 0x80
    a = (a << 1) & 0xff
    if (hi) a ^= POLY
    b >>= 1
  }
  return p
}

function rsRemainder(data: number[], eccCount: number): number[] {
  const gen = [1]
  let genRoot = 1
  for (let i = 0; i < eccCount; i += 1) {
    const next = new Array<number>(gen.length + 1).fill(0)
    for (let j = 0; j < gen.length; j += 1) {
      const coefficient = gen[j]
      const left = next[j]
      const right = next[j + 1]
      if (coefficient === undefined || left === undefined || right === undefined) continue
      next[j] = left ^ coefficient
      next[j + 1] = right ^ gfMul(coefficient, genRoot)
    }
    gen.splice(0, gen.length, ...next)
    genRoot = gfMul(genRoot, 2)
  }
  const rest = data.concat(new Array(eccCount).fill(0))
  for (let i = 0; i < data.length; i += 1) {
    const factor = rest[i]
    if (!factor) continue
    for (let j = 0; j < gen.length; j += 1) {
      const coefficient = gen[j]
      const current = rest[i + j]
      if (coefficient === undefined || current === undefined) continue
      rest[i + j] = current ^ gfMul(coefficient, factor)
    }
  }
  return rest.slice(data.length)
}

function placeFinder(matrix: boolean[][], row: number, col: number): void {
  for (let r = -1; r <= 7; r += 1) {
    for (let c = -1; c <= 7; c += 1) {
      const rr = row + r
      const cc = col + c
      if (rr < 0 || cc < 0 || rr >= SIZE || cc >= SIZE) continue
      const inCore = r >= 0 && r <= 6 && c >= 0 && c <= 6
      const dark =
        inCore &&
        (r === 0 || r === 6 || c === 0 || c === 6 || (r >= 2 && r <= 4 && c >= 2 && c <= 4))
      matrix[rr]![cc] = dark
    }
  }
}

function isReserved(row: number, col: number): boolean {
  if (row <= 8 && col <= 8) return true
  if (row <= 8 && col >= SIZE - 8) return true
  if (row >= SIZE - 8 && col <= 8) return true
  if (row === 6 || col === 6) return true
  if (row === 8 && (col <= 8 || col >= SIZE - 8)) return true
  if (col === 8 && (row <= 8 || row >= SIZE - 8)) return true
  return false
}

function setFormat(matrix: boolean[][]): void {
  // ECC-M + mask 0, BCH-encoded, XOR 0x5412
  const bits = 0b101010000010010
  const coords: Array<[number, number]> = []
  for (let i = 0; i <= 5; i += 1) coords.push([8, i])
  coords.push([8, 7], [8, 8], [7, 8])
  for (let i = 5; i >= 0; i -= 1) coords.push([i, 8])
  for (let i = 0; i < 15; i += 1) {
    const bit = Boolean((bits >> i) & 1)
    const [r, c] = coords[i]!
    matrix[r]![c] = bit
  }
  for (let i = 0; i < 8; i += 1) {
    matrix[8]![SIZE - 1 - i] = Boolean((bits >> i) & 1)
  }
  for (let i = 0; i < 7; i += 1) {
    matrix[SIZE - 7 + i]![8] = Boolean((bits >> (i + 8)) & 1)
  }
  matrix[SIZE - 8]![8] = true
}

function packData(payload: string): number[] {
  const bytes = Array.from(payload, (ch) => ch.charCodeAt(0) & 0xff)
  if (bytes.length > 14) {
    throw new Error('QR payload exceeds version-1 capacity')
  }
  const bits: number[] = []
  const push = (value: number, width: number) => {
    for (let i = width - 1; i >= 0; i -= 1) bits.push((value >> i) & 1)
  }
  push(0b0100, 4)
  push(bytes.length, 8)
  for (const b of bytes) push(b, 8)
  push(0, Math.min(4, 8 * 16 - bits.length))
  while (bits.length % 8 !== 0) bits.push(0)
  const data: number[] = []
  for (let i = 0; i < bits.length; i += 8) {
    let v = 0
    for (let j = 0; j < 8; j += 1) v = (v << 1) | (bits[i + j] ?? 0)
    data.push(v)
  }
  const pad = [0xec, 0x11]
  let p = 0
  while (data.length < 16) {
    data.push(pad[p % 2]!)
    p += 1
  }
  return data.concat(rsRemainder(data, 10))
}

/**
 * Returns a 21×21 matrix (true = dark module) for `payload`.
 * Mask 0: invert modules where (r + c) is even, skipping function patterns.
 */
export function encodeQrMatrix(payload: string): boolean[][] {
  const matrix = Array.from({ length: SIZE }, () => Array<boolean>(SIZE).fill(false))
  placeFinder(matrix, 0, 0)
  placeFinder(matrix, 0, SIZE - 7)
  placeFinder(matrix, SIZE - 7, 0)
  for (let i = 8; i < SIZE - 8; i += 1) {
    matrix[6]![i] = i % 2 === 0
    matrix[i]![6] = i % 2 === 0
  }
  setFormat(matrix)

  const codewords = packData(payload)
  const bits: number[] = []
  for (const word of codewords) {
    for (let i = 7; i >= 0; i -= 1) bits.push((word >> i) & 1)
  }

  let bit = 0
  let upward = true
  for (let col = SIZE - 1; col > 0; col -= 2) {
    if (col === 6) col -= 1
    for (let i = 0; i < SIZE; i += 1) {
      const row = upward ? SIZE - 1 - i : i
      for (const c of [col, col - 1]) {
        if (c < 0 || isReserved(row, c) || matrix[row]![c]) continue
        const raw = bits[bit] === 1
        bit += 1
        const mask = (row + c) % 2 === 0
        matrix[row]![c] = mask ? !raw : raw
      }
    }
    upward = !upward
  }
  return matrix
}

export function parseHandoverQrPayload(raw: string): string | null {
  const compact = raw.toUpperCase().replace(/[^A-Z0-9]/g, '')
  if (compact.length < 6) return null
  return compact.slice(-6)
}
